import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Office, Option, UiArguments } from "@recore-solutions/nstar-lib";
import { Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CodeTableService } from "src/app/listing/code-table.service";
import { ListingService } from "./listing.service";
import { LoonFnFactoriesService } from "./loon-fn-factories.service";

@Injectable({ providedIn: "root" })
export class OfficeFnFactoriesService extends LoonFnFactoriesService {
	constructor(
		protected listingService: ListingService,
		protected codeTableService?: CodeTableService
	) {
		super(listingService, codeTableService);
	}

	queryTransformFn = (uiArguments: UiArguments) => {
		const children = this.getChildrenFromUiArguments(uiArguments);
		const dependents = children.map(this.getDependentOf(uiArguments));
		const dependentControls = dependents.map((c) =>
			uiArguments.formGroup.getColumnByUiComponent(c, uiArguments.keys)
		);
		return (term, additionalParams) => {
			if (!term) {
				return null;
			}
			if (!additionalParams?.every((a) => !!a)) {
				additionalParams = dependentControls.map((c) => c.value?.key || c.value);
			}

			if (!!term && typeof term === "number") {
				if (term < 1) {
					return null;
				}
				return new HttpParams({
					fromObject: {
						[uiArguments.uiComponent.databaseTable + "." + uiArguments.uiComponent.databaseColumn]:
							(term as number).toString(),
					},
				});
			} else {
				if (!(additionalParams || []).every((a) => !!a)) {
					return null;
				}
				let httpParams = new HttpParams();
				if (!term) {
					httpParams = httpParams.set("OfficeMember.PrimaryOffice_IND", "Y");
				} else {
					const searchTerm = ["", ...term.split(" "), ""].join("%");
					httpParams = httpParams.set("Office.FullTextSearch", searchTerm);
				}
				httpParams = children.reduce(
					(a, c, i) =>
						additionalParams?.[i]
							? a.set(
									c.databaseTable + "." + c.databaseColumn,
									additionalParams[i]?.key ? additionalParams[i].key : additionalParams[i]
							  )
							: a,
					httpParams
				);
				return httpParams;
			}
		};
	};

	searchFn = (uiArguments: UiArguments) => {
		const parent = uiArguments.uiComponents.find(
			(u) => u.applicationUIComponentSK === uiArguments.uiComponent.parentApplicationUIComponentSK
		);
		return (queryObj: HttpParams) => {
			const columnPath = [parent.databaseTable, 0, parent.applicationPropertyName];
			const currentSelection = uiArguments.formGroup.get(columnPath)?.value;
			const currentOrNone = [];
			if (!queryObj) {
				return of(currentOrNone);
			}
			let queryObj$: Observable<HttpParams>;
			let mapInParams: (office: Office) => Office = (office) => office;
			if (queryObj.has("Office.Office_SK")) {
				queryObj$ = of(queryObj);
			} else {
				queryObj$ = of(queryObj.set("OfficeMember.VirtualRelationship_IND", "N"));
				mapInParams = (office) => ({
					...office,
					_updateRelated: (currentSelection?.officeSK || currentSelection) !== office.officeSK,
					memberSK: queryObj.get("OfficeMember.Member_SK"),
				});
			}
			return queryObj$.pipe(
				switchMap((params) =>
					!params
						? of([])
						: this.listingService.getGenericProcValues("Roster.spGetRosterOfficeLightAPI", params, [
								{
									recordType: "AssociationOffice",
									newProperty: "boardCD",
									key: "officeSK",
									params: [
										{
											primaryAssociationIND: "Y",
										},
									],
									returnKey: "boardCD",
								},
								{
									recordType: "AssociationOffice",
									newProperty: "officePhone",
									key: "officeSK",
									params: [
										{
											primaryAssociationIND: "Y",
										},
									],
									returnKey: "officePhone",
								},
						  ])
				),
				map((offices) => offices.map(mapInParams)),
				map((offices) => (offices || []).concat(currentOrNone))
			);
		};
	};

	resultTransformFn = () => (results: any[]) => {
		return results.map((r) => ({
			...r,
			key: r.officeSK,
			value: `${r.officeName} (${r.officeNumber}) ${r.boardCD}`,
			sortOrder: 0,
		}));
	};

	resultFindFn = () => (value: any) => (option: Option & any) => {
		return value === option?.key;
	};
}
